import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext
} from '@ngxs/store';
import { AddDeliveryAddress, SetDeliveryAddresses } from './delivery.actions';
import {
  WooCommerceDatetimeDeliveryDate,
  WooCommerceDeliveryAddress
} from '../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import { convertToString } from '../../utils/date.utils';
import { array2Object } from '../../utils/convert-types.utils';
import { findNotEmptyField } from '../../utils/array.util';

export interface DeliveryStateModel {
  addresses: WooCommerceDeliveryAddress[];
}

const defaults: DeliveryStateModel = {
  addresses: []
};

@State<DeliveryStateModel>({
  name: 'delivery',
  defaults
})
@Injectable()
export class DeliveryState {
  static dateTimeSettings(addressId: string, shippingMethodId?: string) {
    return createSelector([DeliveryState], (state: DeliveryStateModel) => {
      const pickupOrderLocation: WooCommerceDeliveryAddress =
        state.addresses.find(({ id }: WooCommerceDeliveryAddress): boolean => {
          return addressId === id;
        })!;

      if (!pickupOrderLocation || !pickupOrderLocation.shipping_methods) {
        return null;
      }

      if (shippingMethodId) {
        const shippingMethodObject = array2Object(
          pickupOrderLocation.shipping_methods
        );

        const shippingMethod = shippingMethodObject[shippingMethodId];
        return shippingMethod?.datetime_settings || null;
      } else {
        const shippingMethod = findNotEmptyField(
          'datetime_settings',
          pickupOrderLocation.shipping_methods
        );

        return shippingMethod?.datetime_settings || null;
      }
    });
  }

  static scheduleTimeSlots(
    addressId: string,
    date: Date,
    shippingMethodId?: string
  ) {
    return createSelector([DeliveryState], (state: DeliveryStateModel) => {
      const pickupOrderLocation: WooCommerceDeliveryAddress =
        state.addresses.find(({ id }: WooCommerceDeliveryAddress): boolean => {
          return addressId === id;
        })!;

      if (!pickupOrderLocation || !pickupOrderLocation.shipping_methods) {
        return [];
      }

      let deliveryDates: WooCommerceDatetimeDeliveryDate[];
      if (shippingMethodId) {
        const shippingMethodObject = array2Object(
          pickupOrderLocation.shipping_methods
        );
        const shippingMethod = shippingMethodObject[shippingMethodId];
        deliveryDates = shippingMethod?.datetime_settings?.delivery_dates || [];
      } else {
        const shippingMethod = findNotEmptyField(
          'datetime_settings',
          pickupOrderLocation.shipping_methods
        );

        deliveryDates = shippingMethod?.datetime_settings?.delivery_dates || [];
      }

      return (
        deliveryDates.find(
          (deliveryDate: WooCommerceDatetimeDeliveryDate): boolean => {
            return deliveryDate.date === convertToString(date);
          }
        )?.time_slots || []
      );
    });
  }

  @Selector()
  static deliveryAddresses(state: DeliveryStateModel) {
    return state.addresses;
  }

  @Action(SetDeliveryAddresses)
  setDeliveryAddresses(
    { patchState }: StateContext<DeliveryStateModel>,
    { payload }: SetDeliveryAddresses
  ) {
    patchState({ addresses: payload });
  }

  @Action(AddDeliveryAddress)
  addDeliveryAddress(
    { getState, patchState }: StateContext<DeliveryStateModel>,
    { payload }: AddDeliveryAddress
  ) {
    const state: DeliveryStateModel = getState();
    patchState({ addresses: [...state.addresses, payload] });
  }
}
